const allBenchmarkValues = {
  // last updated 2025-01-20 (for entire 2024)
  autonomy: 8.3,
  competence: 8.0,
  engagement: 8.1,
  enps: 5, // fake guess
  feedback: 7.8,
  participation: 86,
  puls_communication: 7.6,
  puls_competence: 7.7,
  puls_culture_and_values: 8.4,
  puls_enps: 7.6,
  puls_goals: 8.5,
  puls_happiness_and_wellbeing: 8.3,
  puls_leadership: 7.9,
  puls_motivation: 8.3,
  puls_our_processes: 7.0,
  puls_roles_and_responsibilities: 8.2,
  puls_stress: 7.3,
  puls_teamwork: 8.6,
  puls_work_environment: 8.6,
  relatedness: 8.1,
  trust: 7.9,
  wellbeing: 7.4,
  puls_health: 7.3,
  puls_development_opportunities: 7.4, // not present in enough surveys in 2024
  puls_food_exercise: 8.0 // not present in enough surveys in 2024
  // TODO: Move this to backend and maybe also include question benchmarks.
}

export const comparedToBenchmarkValue = ({
  currentItemName,
  currentValue,
  benchmarkValues
}) => {
  if (typeof currentValue === 'string') {
    throw new Error('should be a number ')
  }
  if (Object.keys(benchmarkValues).includes(currentItemName)) {
    const buffer = 0.05
    if (
      currentValue + buffer > benchmarkValues[currentItemName] &&
      currentValue - buffer > benchmarkValues[currentItemName]
    ) {
      return 'higherText'
    } else if (
      currentValue + buffer < benchmarkValues[currentItemName] &&
      currentValue - buffer < benchmarkValues[currentItemName]
    ) {
      return 'lowerText'
    }
    return 'equalText'
  }
  return null
}

const defaultComparator = (a, b) => {
  const rootA = !a.externalParentId
  const rootB = !b.externalParentId

  if (rootA && !rootB) {
    return -1 // a should come before b
  } else if (!rootA && rootB) {
    return 1 // b should come before a
  }
  return a.messages.en.title.localeCompare(b.messages.en.title)
}

function depthFirstTreeSort(arr, cmp) {
  const results = []
  const tree = {}

  const isRootGroup = (group) =>
    !group.externalParentId ||
    !arr.find((g) => g.externalId === group.externalParentId)

  // Build the tree
  arr.forEach((item) => {
    const parentId = isRootGroup(item) ? 'root' : item.externalParentId
    if (!tree[parentId]) {
      tree[parentId] = []
    }
    tree[parentId].push(item)
  })

  // Depth-first traversal to sort and collect nodes
  function depthFirstTraversal(nodeId, i = 0) {
    const children = tree[nodeId]
    if (children) {
      children.sort(cmp) // Sort children of the current node
      children.forEach((child) => {
        child.level = i
        results.push(child) // Process the current child
        depthFirstTraversal(child.externalId, i + 1) // Recursively process each child
      })
    }
  }

  // Start traversal from the root
  if (tree.root) {
    tree.root.sort(cmp) // Sort root nodes before starting
    depthFirstTraversal('root')
  }

  return results
}

export const sortedGroups = (arr, cmp = defaultComparator) => {
  const safeArray = JSON.parse(JSON.stringify(arr))
  return depthFirstTreeSort(safeArray, cmp)
}
export const allChildGroupsRecursively = ({ group, groups }) => {
  if (!group.externalId) {
    return groups // for entire org
  }

  const result = []
  const visited = new Set()
  const getKey = (g) => g.externalId || '__root__'

  const traverse = (parent) => {
    const key = getKey(parent)
    if (visited.has(key)) return
    visited.add(key)

    const children = groups.filter((child) =>
      parent.externalId
        ? child.externalParentId === parent.externalId
        : !child.externalParentId
    )

    children.forEach((child) => {
      result.push(child)
      traverse(child)
    })
  }

  traverse(group)
  return result
}

export const getForceIncludedAndNotExcludedCategories = ({
  categoryName,
  categoryOptions,
  categoriesResult,
  surveyDetailsCategories
}) => {
  return surveyDetailsCategories
    .filter(
      (category) =>
        Object.keys(categoriesResult).find(
          (categoryName) => categoryName === category.name
        ) || categoryOptions.forceIncludedCategories?.includes(category.name)
    )
    .filter(
      (category) =>
        !categoryOptions.excludedCategories ||
        !categoryOptions.excludedCategories.includes(category.name) ||
        (categoryName &&
          categoryOptions.excludedCategories.includes(categoryName)) // allow excluded category if its in the route name
    )
}

export const resultViewsContainsActionsInAlerts = (details) => {
  const rows = details.resultViews.user.rows.concat(
    details.resultViews.admin.rows
  )

  const wrapperRows = rows.reduce((acc, row) => {
    const wrappers = row.cols.filter((col) => col.type === 'wrapper')
    wrappers.forEach((wrapper) => {
      acc.push(...wrapper.data.rows)
    })
    return acc
  }, [])
  rows.push(...wrapperRows)

  return rows.find((row) => {
    return row.cols.find((col) => {
      const rulesProperty = col.data.rules ? 'rules' : 'tags'
      return (
        col.type === 'alert_box' &&
        col.data[rulesProperty] &&
        col.data[rulesProperty].find((t) => t.actionCategories)
      )
    })
  })
}

/**
 *
 * @param item: { name, title, value }
 *
 * @returns
 */
export const getComparedToBenchmarkText = ({
  t,
  tr,
  localeNumber,
  item,
  shouldBeZeroToHundred = false
}) => {
  const itemTitle = item.title || tr(item.messages, 'title')
  const benchmarkValues = allBenchmarkValues
  const benchmarkValue = benchmarkValues[item.name]

  const roundingDigits = shouldBeZeroToHundred ? 0 : 1

  const fractionalDigits = shouldBeZeroToHundred ? 0 : 1

  const benchmarkDiff =
    Math.abs(benchmarkValue - item.value)?.toFixed(roundingDigits) || null

  const comparisonText = comparedToBenchmarkValue({
    currentItemName: item.name,
    currentValue: item.value,
    benchmarkValues
  })
  const comparisonTextToKey = {
    higherText: 'higherThanBenchmarkValue',
    lowerText: 'lowerThanBenchmarkValue',
    equalText: 'equalsToBenchmarkValue'
  }
  if (comparisonText) {
    const diffObj = {
      title: itemTitle,
      benchmarkDiff: localeNumber(benchmarkDiff, fractionalDigits),
      benchmarkValue: localeNumber(benchmarkValue, fractionalDigits)
    }

    const formatAsPercentage = item.name === 'participation'
    if (formatAsPercentage) {
      diffObj.benchmarkValue = `${benchmarkValue} %`
      diffObj.benchmarkDiff = `${benchmarkDiff}`
    }

    return t(
      `resultDetails.tagDetails.currentValue.${comparisonTextToKey[comparisonText]}`,
      diffObj
    )
  }
  return null
}

export const processAmounts = (target, statItem, callback) => {
  for (let i = 0; i <= 10; i++) {
    const amountKey = `amount_${i}`
    const amount = statItem[amountKey] || 0
    callback(target, i, amount)
  }
}
